// API base URL - use production URL in development and relative path in production
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'https://kalahats.com/api';

/**
 * Fetch all custom orders
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Custom orders and pagination info
 */
export async function fetchCustomOrders(params = {}) {

  try {
    // Build query string
    const queryParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      queryParams.append(key, value);
    }
    
    const response = await fetch(`${API_BASE_URL}/custom-orders?${queryParams.toString()}`);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching custom orders:', error);
    throw error;
  }
}

/**
 * Fetch a custom order by ID
 * @param {number|string} id - Custom order ID
 * @returns {Promise<Object|null>} Custom order object or null if not found
 */
export async function fetchCustomOrderById(id) {

  try {
    const response = await fetch(`${API_BASE_URL}/custom-orders/${id}`);

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error fetching custom order with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Create a new custom order
 * @param {Object} customOrderData - Custom order data
 * @returns {Promise<Object>} Created custom order
 */
export async function createCustomOrder(customOrderData) {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-orders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(customOrderData),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating custom order:', error);
    throw error;
  }
}

/**
 * Update a custom order
 * @param {number|string} id - Custom order ID
 * @param {Object} customOrderData - Updated custom order data
 * @returns {Promise<Object>} Updated custom order
 */
export async function updateCustomOrder(id, customOrderData) {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-orders/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(customOrderData),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error updating custom order with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Delete a custom order
 * @param {number|string} id - Custom order ID
 * @returns {Promise<boolean>} Success status
 */
export async function deleteCustomOrder(id) {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-orders/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    const result = await response.json();
    return result.success;
  } catch (error) {
    console.error(`Error deleting custom order with ID ${id}:`, error);
    throw error;
  }
}
